import { dbService } from '../firebaseInstance';

/**
 * 사용자의 관리자 권한을 확인합니다.
 * @param {string} userID - 확인할 사용자 ID
 * @returns {Promise<boolean>} 관리자 권한 여부
 */
export const checkAdminPermission = async (userID) => {
  try {
    if (!userID) return false;
    
    const adminDoc = await dbService.collection('admin').doc('adminUsers').get();
    if (!adminDoc.exists) {
      console.log('Admin collection does not exist');
      return false;
    }
    
    const adminData = adminDoc.data();
    const isAdmin = adminData.userIDs && adminData.userIDs.includes(userID);
    
    console.log(`Admin permission check for ${userID}: ${isAdmin}`);
    return isAdmin;
  } catch (error) {
    console.error('Admin permission check failed:', error);
    return false;
  }
};

/**
 * 관리자 목록을 가져옵니다.
 * @returns {Promise<string[]>} 관리자 UID 목록
 */
export const getAdminUsers = async () => {
  try {
    const adminDoc = await dbService.collection('admin').doc('adminUsers').get();
    if (!adminDoc.exists) return [];
    
    const adminData = adminDoc.data();
    return adminData.userIDs || [];
  } catch (error) {
    console.error('Get admin users failed:', error);
    return [];
  }
};

/**
 * 관리자를 추가합니다. (수퍼 관리자만 사용 가능)
 * @param {string} newAdminUID - 추가할 관리자 UID
 * @param {string} addedBy - 추가한 사용자 UID
 * @returns {Promise<boolean>} 성공 여부
 */
export const addAdminUser = async (newAdminUID, addedBy) => {
  try {
    // 추가하는 사용자가 관리자인지 확인
    const isAdmin = await checkAdminPermission(addedBy);
    if (!isAdmin) {
      console.error('Only admins can add new admins');
      return false;
    }
    
    const adminRef = dbService.collection('admin').doc('adminUsers');
    const adminDoc = await adminRef.get();
    
    if (!adminDoc.exists) {
      // admin 컬렉션이 없으면 생성
      await adminRef.set({
        userIDs: [newAdminUID],
        lastUpdated: new Date(),
        createdBy: addedBy
      });
    } else {
      // 기존 관리자 목록에 추가
      const adminData = adminDoc.data();
      const userIDs = adminData.userIDs || [];
      
      if (!userIDs.includes(newAdminUID)) {
        userIDs.push(newAdminUID);
        await adminRef.update({
          userIDs: userIDs,
          lastUpdated: new Date()
        });
      }
    }
    
    console.log(`Admin user ${newAdminUID} added successfully`);
    return true;
  } catch (error) {
    console.error('Add admin user failed:', error);
    return false;
  }
};

/**
 * 관리자를 제거합니다. (수퍼 관리자만 사용 가능)
 * @param {string} adminUID - 제거할 관리자 UID
 * @param {string} removedBy - 제거한 사용자 UID
 * @returns {Promise<boolean>} 성공 여부
 */
export const removeAdminUser = async (adminUID, removedBy) => {
  try {
    // 제거하는 사용자가 관리자인지 확인
    const isAdmin = await checkAdminPermission(removedBy);
    if (!isAdmin) {
      console.error('Only admins can remove admins');
      return false;
    }
    
    const adminRef = dbService.collection('admin').doc('adminUsers');
    const adminDoc = await adminRef.get();
    
    if (!adminDoc.exists) return false;
    
    const adminData = adminDoc.data();
    const userIDs = adminData.userIDs || [];
    
    // 자신을 제거하려는 경우 방지
    if (adminUID === removedBy) {
      console.error('Cannot remove yourself from admin');
      return false;
    }
    
    const filteredUserIDs = userIDs.filter(id => id !== adminUID);
    
    await adminRef.update({
      userIDs: filteredUserIDs,
      lastUpdated: new Date()
    });
    
    console.log(`Admin user ${adminUID} removed successfully`);
    return true;
  } catch (error) {
    console.error('Remove admin user failed:', error);
    return false;
  }
};

/**
 * 초기 관리자 설정 (최초 1회만 실행)
 * @param {string} initialAdminUID - 초기 관리자 UID
 * @returns {Promise<boolean>} 성공 여부
 */
export const initializeAdminSystem = async (initialAdminUID) => {
  try {
    const adminRef = dbService.collection('admin').doc('adminUsers');
    const adminDoc = await adminRef.get();
    
    if (adminDoc.exists) {
      console.log('Admin system already initialized');
      return false;
    }
    
    await adminRef.set({
      userIDs: [initialAdminUID],
      lastUpdated: new Date(),
      createdBy: 'system',
      isInitialized: true
    });
    
    console.log(`Admin system initialized with ${initialAdminUID}`);
    return true;
  } catch (error) {
    console.error('Initialize admin system failed:', error);
    return false;
  }
}; 